import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import classnames from 'classnames';
import { formatPhoneNumber } from 'common/formatters';

import {
  USER_ROLE_RENTER,
} from 'common/userRoles';

import {
  SR_API_ACTION_REQUEST_TOUR_SHOWING,
  SR_API_ACTION_VERIFY_PHONE_SMS_CODE_REQUESTED,
  SR_API_ACTION_VERIFY_PHONE_SMS_CODE_SUBMIT,
} from 'api/actionTypes';

import {
  SCREEN_ID_TOUR_VERIFY_PHONE,
} from 'client/constants/screenIds';

import AuthActions from 'client/actions/Auth';
import ListingActions from 'client/actions/Listing';
import HouseholdActions from 'client/actions/Household';
import NavigatorActions from 'client/actions/Navigator';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import allowedRoles from 'client/helpers/allowedRoles';

import ButtonDeprecated from 'client/components/ButtonDeprecated';
import InputText from 'client/components/InputText';
import TourActions from 'client/actions/Tour';
import Navbar from 'client/components/Navbar';
import BackButton from 'client/components/BackButton';
import SrSmsSendingIndicator from 'client/components/SrSmsSendingIndicator';

import branch from 'client/core/branch';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  verifyPhoneCodeRequestedApiState: {
    action: SR_API_ACTION_VERIFY_PHONE_SMS_CODE_REQUESTED,
  },
  verifyPhoneCodeSubmitApiState: {
    action: SR_API_ACTION_VERIFY_PHONE_SMS_CODE_SUBMIT,
  },
  requestShowingApiState: {
    action: SR_API_ACTION_REQUEST_TOUR_SHOWING,
  },
})
@branch({
  currentUser: ['currentUser'],
})
@withScreenId(SCREEN_ID_TOUR_VERIFY_PHONE)
class VerifyPhonePage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Sunroom Rentals',
    section: 'Schedule Showing',
  })

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    verifyPhoneCodeRequestedApiState: PropTypes.object.isRequired,
    verifyPhoneCodeSubmitApiState: PropTypes.object.isRequired,
    requestShowingApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      phoneInput: props.currentUser.phone || '',
      codeInput: '',
      codeRequested: false,
      incorrectCodeSubmitted: false,
      showSmsSending: false,
      redirectSource: locationQuery.source,
    };
  }

  componentDidMount() {
    const { currentUser } = this.props;
    if (currentUser.phoneVerified) {
      this._onPhoneVerified();
    }
  }

  async _requestVerificationCode(phone) {
    const {
      dispatch,
    } = this.props;

    this.setState({
      showSmsSending: true,
      incorrectCodeSubmitted: false,
    });
    setTimeout(() => {
      if (!this._unmounting) {
        this.setState({
          showSmsSending: false,
          codeRequested: true,
        });
      }
    }, 3000);

    await dispatch(AuthActions.verifyPhoneSmsCodeRequested, {
      phone: phone,
    });
  }

  _resendVerificationCode = (event) => {
    const {
      phoneInput,
    } = this.state;

    event.preventDefault();
    event.stopPropagation();

    this.setState({
      incorrectCodeSubmitted: false,
    });

    this._requestVerificationCode(phoneInput);
  }

  async _verifyCode(phoneInput, codeInput) {
    const {
      dispatch,
    } = this.props;

    const isVerified = await dispatch(AuthActions.verifyPhoneSmsCodeSubmit, {
      phone: phoneInput,
      code: codeInput,
    });

    this.setState({
      incorrectCodeSubmitted: !isVerified,
    });

    if (isVerified) {
      this._onPhoneVerified();
    }
  }

  _onHandleSubmitRequest = (event) => {
    const {
      phoneInput,
    } = this.state;

    event.preventDefault();
    this._requestVerificationCode(phoneInput);
  }

  _onHandleSubmitVerify = (event) => {
    const {
      phoneInput,
      codeInput,
    } = this.state;

    event.preventDefault();
    this._verifyCode(phoneInput, codeInput);
  }

  _onPhoneInputChange = (event) => {
    const phoneInputValue = event.target.value.trim() || '';
    if (phoneInputValue.length <= 10 && !isNaN(Number(phoneInputValue))) {
      this.setState({
        phoneInput: phoneInputValue,
      });
    }
  }

  _onCodeInputChange = (event) => {
    const codeInputValue = event.target.value.trim() || '';
    if (codeInputValue.length <= 4 && !isNaN(Number(codeInputValue))) {
      this.setState({
        codeInput: codeInputValue,
      });
    }
  }

  _onBack = (event) => {
    const { dispatch } = this.props;
    const { codeRequested } = this.state;

    event.preventDefault();

    if (!codeRequested) {
      dispatch(NavigatorActions.goBack, { defaultTo: '/feed' });
      return;
    }

    this.setState({
      codeRequested: false,
    });
  }

  async _onPhoneVerified() {
    const {
      dispatch,
      match,
      location,
    } = this.props;
    const {
      redirectSource,
    } = this.state;

    const locationQuery = queryString.parse(location.search);

    switch (redirectSource) {
      case 'tour': {
        dispatch(TourActions.requestShowingStepSubmit, {
          listingId: parseInt(match.params.listingId, 10),
          locationQuery: locationQuery,
        });
        break;
      }
      case 'checkAvailability': {
        dispatch(ListingActions.checkAvailabilityButtonClick, {
          listingId: parseInt(match.params.listingId, 10),
        });
        break;
      }
      case 'roommateSignup': {
        dispatch(NavigatorActions.replace, { to: '/tour' });
        dispatch(HouseholdActions.roommateApplicationStep, {
          listingId: parseInt(match.params.listingId, 10),
        });
        break;
      }
      case 'guarantorSignup': {
        dispatch(HouseholdActions.startGuarantorApplication, {
          householdId: parseInt(locationQuery.householdId, 10),
          listingId: parseInt(locationQuery.listingId, 10),
        });
        break;
      }
      case 'unlockListing': {
        dispatch(NavigatorActions.replace, {
          to: `/listing/${match.params.listingId}?${queryString.stringify({
            ...locationQuery,
            source: 'unlockListing',
          })}`,
        });
        break;
      }
      default: {
        dispatch(NavigatorActions.replace, { to: '/explore/map' });
      }
    }
  }

  render() {
    const {
      verifyPhoneCodeRequestedApiState,
      verifyPhoneCodeSubmitApiState,
      requestShowingApiState,
    } = this.props;

    const {
      phoneInput,
      codeInput,
      showSmsSending,
      incorrectCodeSubmitted,
      codeRequested,
    } = this.state;

    const isBusy = (
      requestShowingApiState.inProgress
      || verifyPhoneCodeRequestedApiState.inProgress
      || verifyPhoneCodeSubmitApiState.inProgress
    );

    return (
      <div className='page-scheduleTourConfirmPhone'>
        {showSmsSending && (
          <SrSmsSendingIndicator
            phone={phoneInput}
          />
        )}
        <div className='moveInDate'>
          <Navbar
            leftButton={(
              <BackButton
                onClick={this._onBack}
              />
            )}
          />
          {codeRequested
            ? (
              <div className='stepContainer'>
                <div className='textContainer'>
                  <h1>Enter your 4-digit code</h1>
                  <span className='explainerText'>
                    We just texted the code to {formatPhoneNumber(phoneInput)}.
                  </span>
                </div>
                <div className='formContainer answers'>
                  <InputText
                    placeholder='1234'
                    onChange={this._onCodeInputChange}
                    type='tel'
                    pattern='[0-9]*'
                    inputmode='numeric'
                    value={codeInput}
                  />
                  <ButtonDeprecated
                    type='submit'
                    onClick={this._onHandleSubmitVerify}
                    disabled={isBusy || codeInput.length !== 4}
                  >
                    {isBusy ? 'loading...' : 'verify'}
                  </ButtonDeprecated>
                </div>
                <div className='resendCodeContainer'>
                  <div
                    className={
                      classnames(
                        incorrectCodeSubmitted && 'formMessage error',
                      )
                    }
                    onClick={this._resendVerificationCode}
                  >
                    {incorrectCodeSubmitted
                      ? (
                        <span>
                          That's not the right code! Click to send another.
                        </span>
                      ) : (
                        <span className='resend'>
                          Didn't get the code? Click to send another.
                        </span>
                      )}
                  </div>
                </div>
              </div>
            ) : (
              <div className='stepContainer'>
                <div className='textContainer'>
                  <h1>Verify your phone number</h1>
                  <span className='explainerText'>We'll text you a code to verify this number.</span>
                </div>
                <div className='formContainer answers'>
                  <InputText
                    placeholder='Enter your phone number'
                    onChange={this._onPhoneInputChange}
                    type='tel'
                    pattern='[0-9]*'
                    inputmode='numeric'
                    value={phoneInput}
                  />
                  <ButtonDeprecated
                    type='submit'
                    onClick={this._onHandleSubmitRequest}
                    disabled={isBusy || phoneInput.length !== 10}
                  >
                    {isBusy ? 'loading...' : 'send text'}
                  </ButtonDeprecated>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

VerifyPhonePage.displayName = 'VerifyPhonePage';

export default VerifyPhonePage;
